// Agregado de la hoja de produccion: logica pura para poder testearla.
//
// Tres vueltas de tuerca sobre lo que devuelve la base:
//
// 1. UNIDADES. El pedido se toma en paquetes (bultos, kilos), pero la cuadra
//    hornea unidades: `unidades = bultos * empaque`. Por eso la flauta que se
//    factura por kilo lleva empaque = 3 (en un kilo entran 3 flautas): el
//    remito sigue diciendo kilos y el panadero ve flautas.
//
// 2. GRUPOS. "Pan Galleta Campana", "Pan Galleta Campana x4" y "Galleta
//    Campana x8" son el mismo pan en distinto envase. Si comparten grupo salen
//    en UNA fila con el total de unidades y el desglose abajo, que es como los
//    panaderos lo venian sumando a mano.
//
// 3. FRACCIONES. Un pedido puede venir en medios kilos (2,5 kg de flauta =
//    7,5 flautas) pero del horno no sale media flauta: el total de cada fila se
//    redondea PARA ARRIBA y se guarda aparte el valor exacto, que la pantalla
//    muestra al lado para que se vea de donde salio. Ojo: se redondea solo lo
//    que va al horno; el pedido, el remito y la cuenta del cliente siguen con
//    los 2,5 kilos y su precio exacto.

import type { ProduccionItem, ProduccionDetalle } from "./types";

/** Una parte de una fila agrupada (el envase que aporto esas unidades). */
export interface ParteProduccion {
  producto_id: number;
  descripcion: string;
  empaque: number;
  bultos: number;
  unidades: number;
}

/** Fila de la hoja: un articulo suelto o un grupo con sus partes. */
export interface FilaProduccion {
  clave: string; // 'g3' (grupo) | 'p42' (producto suelto)
  etiqueta: string;
  area: string;
  esGrupo: boolean;
  bultos: number;           // paquetes o kilos pedidos (puede ser fraccionado)
  unidades: number;         // lo que va al horno: redondeado PARA ARRIBA
  unidadesExactas: number;  // sin redondear (igual a `unidades` si dio entero)
  partes: ParteProduccion[];
}

export interface BloqueArea {
  area: string;
  filas: FilaProduccion[];
}

const claveDe = (it: ProduccionItem): string =>
  it.grupo_id ? `g${it.grupo_id}` : `p${it.producto_id}`;

/**
 * Agrupa los items por area y, dentro de cada area, colapsa en una sola fila
 * los articulos que comparten grupo de produccion.
 *
 * Un grupo cuyos articulos esten repartidos en varias areas se agrupa dentro de
 * cada area (el area la trabaja gente distinta: mezclarlas seria peor).
 */
export function agruparProduccion(items: ProduccionItem[]): BloqueArea[] {
  const areas = new Map<string, Map<string, FilaProduccion>>();

  for (const it of items) {
    const filas = areas.get(it.area) ?? new Map<string, FilaProduccion>();
    areas.set(it.area, filas);

    const clave = claveDe(it);
    const fila =
      filas.get(clave) ??
      ({
        clave,
        etiqueta: it.grupo_id ? (it.grupo ?? it.descripcion) : it.descripcion,
        area: it.area,
        esGrupo: Boolean(it.grupo_id),
        bultos: 0,
        unidades: 0,
        unidadesExactas: 0,
        partes: [],
      } satisfies FilaProduccion);

    fila.bultos += it.bultos;
    fila.unidadesExactas += it.unidades;
    fila.partes.push({
      producto_id: it.producto_id,
      descripcion: it.descripcion,
      empaque: it.empaque,
      bultos: it.bultos,
      unidades: it.unidades,
    });
    filas.set(clave, fila);
  }

  // El redondeo va sobre el TOTAL de la fila, no parte por parte: son envases
  // del mismo pan, lo que se hornea es el total.
  for (const filas of areas.values()) {
    for (const fila of filas.values()) {
      fila.unidades = Math.ceil(fila.unidadesExactas);
    }
  }

  return [...areas.entries()].map(([area, filas]) => ({
    area,
    filas: [...filas.values()],
  }));
}

/** Un envase de una fila agrupada de la matriz, con su reparto por cliente. */
export interface ParteMatriz {
  producto_id: number;
  etiqueta: string;
  empaque: number;
  celdas: number[]; // unidades por cliente, en el orden de `columnas`
  total: number;
}

export interface FilaMatriz {
  clave: string;
  etiqueta: string;
  esGrupo: boolean;
  celdas: number[]; // unidades por cliente, en el orden de `columnas`
  total: number;
  partes: ParteMatriz[]; // vacio si no es grupo
}

export interface MatrizProduccion {
  columnas: Array<{ cliente_id: number; cliente: string }>;
  filas: FilaMatriz[];
}

/**
 * Matriz articulo x cliente en UNIDADES, como la planilla que llevaban en
 * Excel: una fila por articulo (o grupo), una columna por cliente del dia y la
 * columna de totales al final.
 *
 * Aca NO se redondea: es el reparto, y tiene que cerrar con lo que se le
 * facturo a cada cliente. El redondeo hacia arriba es solo de la hoja del horno.
 *
 * `detalle` viene en bultos por producto y cliente; el empaque y el grupo salen
 * de `items`, que es la misma fecha y el mismo filtro de pedidos confirmados.
 */
export function matrizProduccion(
  items: ProduccionItem[],
  detalle: ProduccionDetalle[],
): MatrizProduccion {
  // Un producto puede aparecer en varias filas de `detalle` (una por cliente),
  // asi que el indice se arma una sola vez.
  const porProducto = new Map<number, ProduccionItem>();
  for (const it of items) porProducto.set(it.producto_id, it);

  const columnas: Array<{ cliente_id: number; cliente: string }> = [];
  const indiceCliente = new Map<number, number>();
  for (const d of detalle) {
    if (indiceCliente.has(d.cliente_id)) continue;
    indiceCliente.set(d.cliente_id, columnas.length);
    columnas.push({ cliente_id: d.cliente_id, cliente: d.cliente });
  }
  columnas.sort((a, b) => a.cliente.localeCompare(b.cliente, "es"));
  indiceCliente.clear();
  columnas.forEach((c, i) => indiceCliente.set(c.cliente_id, i));

  // Las filas se arman con el mismo orden y las mismas claves que la hoja.
  // Igual que en la hoja del horno, las filas de grupo llevan el desglose por
  // envase (una parte por articulo), aunque el grupo tenga un solo envase.
  const filas = new Map<string, FilaMatriz>();
  const partePorProducto = new Map<number, ParteMatriz>();
  for (const it of items) {
    const clave = claveDe(it);
    const fila =
      filas.get(clave) ??
      ({
        clave,
        etiqueta: it.grupo_id ? (it.grupo ?? it.descripcion) : it.descripcion,
        esGrupo: Boolean(it.grupo_id),
        celdas: columnas.map(() => 0),
        total: 0,
        partes: [],
      } satisfies FilaMatriz);
    filas.set(clave, fila);
    if (!fila.esGrupo) continue;
    const parte: ParteMatriz = {
      producto_id: it.producto_id,
      etiqueta: it.descripcion,
      empaque: it.empaque,
      celdas: columnas.map(() => 0),
      total: 0,
    };
    fila.partes.push(parte);
    partePorProducto.set(it.producto_id, parte);
  }

  for (const d of detalle) {
    const it = porProducto.get(d.producto_id);
    const col = indiceCliente.get(d.cliente_id);
    if (!it || col === undefined) continue; // producto de otra fecha/pedido no confirmado
    const fila = filas.get(claveDe(it));
    if (!fila) continue;
    const unidades = d.bultos * it.empaque;
    fila.celdas[col] += unidades;
    fila.total += unidades;
    const parte = partePorProducto.get(d.producto_id);
    if (parte) {
      parte.celdas[col] += unidades;
      parte.total += unidades;
    }
  }

  return { columnas, filas: [...filas.values()] };
}
